/**
 * services/BaseService.ts
 * Generic CRUD service foundation for all application entities
 *
 * Provides standard database operations with contractor integration hooks
 * for licensing, analytics, and cloud sync. All specific services extend
 * this base to inherit consistent patterns and future-ready architecture.
 *
 * Supports soft-delete (tombstone) pattern for sync-aware entities via
 * the `softDelete()` method. Entities must extend `SyncDeletable` to use
 * soft-delete functionality.
 */

// External imports
import type { Table } from 'dexie';

// Internal imports (@/...)
import { db, AppDatabase } from '@/lib/db';
import type { SyncDeletable } from '@/types/sync';
import { generateUUID } from '@/utils/uuid';

// Relative imports
import { softDeleteRecord } from './sync/SoftDeleteUtils';
import { syncCoordinatorService } from './sync/SyncCoordinatorService';
import { SYNC_TABLES, type SyncTableName } from '@/constants/syncTables';
import { getSyncOperationService } from './sync/SyncOperationService';
import { markAsInstrumentedWriteContext } from './sync/SyncImportContext';
import { buildSetOps, buildDiffOps } from './sync/OperationBuilder';

/**
 * Base entity interface - all persistent entities must have these fields
 */
interface BaseEntity {
  id: string;
  createdAt: string;
  updatedAt: string;
}


/**
 * Generic base service class providing CRUD operations for any entity type
 * 
 * @template T - The entity type this service manages (must extend BaseEntity)
 */
export abstract class BaseService<T extends BaseEntity> {
  protected readonly tableName: string;
  protected readonly table: Table<T>;

  // ===== CONTRACTOR INTEGRATION HOOKS =====
  // These will be implemented by contractor for licensing, analytics, etc.
  
  /** Called before adding new entity - for license checks */
  protected beforeAdd?: (data: Partial<T>) => Promise<void>;
  
  /** Called after successfully adding entity - for analytics tracking */
  protected afterAdd?: (entity: T) => Promise<void>;
  
  /** Called before updating entity - for permission checks */
  protected beforeUpdate?: (id: string, changes: Partial<T>) => Promise<void>;
  
  /** Called after successfully updating entity - for sync triggers */
  protected afterUpdate?: (entity: T) => Promise<void>;
  
  /**
   * Called before deleting entity - for dependency checks.
   * Called for both soft-delete (tombstone) and hard-delete operations.
   */
  protected beforeDelete?: (id: string) => Promise<void>;

  /**
   * Called after successfully deleting entity - for cleanup.
   * Called for both soft-delete (tombstone) and hard-delete operations.
   */
  protected afterDelete?: (id: string) => Promise<void>;

  constructor(tableName: string) {
    this.tableName = tableName;
    
    // Type-safe dynamic table access with runtime validation
    // We need to handle the fact that db[key] could be various types (methods, properties, or tables)
    // Using indexed access with runtime validation ensures safety
    const tableKey = tableName as keyof AppDatabase;
    const potentialTable = db[tableKey];
    
    // Validate that we're accessing a Table instance, not a method or other property
    if (!potentialTable || typeof potentialTable !== 'object' || !('add' in potentialTable)) {
      throw new Error(`Table '${tableName}' not found in database. Check that the table is defined in lib/db.ts`);
    }
    
    // Safe cast after validation - we've confirmed it's a Table-like object
    this.table = potentialTable as unknown as Table<T>;
  }

  // ===== OP LOGGING HELPERS =====
  // These support the operation-based sync architecture (WU4C)

  /**
   * Check if this table participates in sync and needs op logging.
   * Only tables in SYNC_TABLES should have operations logged.
   */
  private isSyncTable(): boolean {
    return SYNC_TABLES.includes(this.tableName as SyncTableName);
  }

  // ===== CORE CRUD OPERATIONS =====

  /**
   * Add a new entity to the database
   *
   * Logs 'set' operations for all fields if table is in SYNC_TABLES.
   * Data write and op logging are atomic (same transaction).
   *
   * @param data - Partial entity data (id, createdAt, updatedAt will be added)
   * @returns Promise resolving to the created entity
   */
  async add(data: Partial<T>): Promise<T> {
    // Hook: beforeAdd (outside transaction - can abort before any changes)
    await this.beforeAdd?.(data);

    const now = new Date().toISOString();
    const entity: T = {
      ...data,
      id: generateUUID(),
      createdAt: now,
      updatedAt: now,
    } as T;

    try {
      // Wrap data write + op logging in single transaction for atomicity
      // Note: logBatch() uses [db.sync_state, db.sync_operations] which is a subset of our tables,
      // so Dexie will join the nested transaction into this one (per Dexie docs).
      await db.transaction('rw', [this.table, db.sync_operations, db.sync_state], async () => {
        markAsInstrumentedWriteContext(); // Suppress safety-net hook warnings
        // table.add() throws ConstraintError if key exists, which aborts transaction
        await this.table.add(entity);

        // Log ops for sync tables only
        if (this.isSyncTable()) {
          const ops = buildSetOps(
            this.tableName as SyncTableName,
            entity.id,
            entity as unknown as Record<string, unknown>
          );
          if (ops.length > 0) {
            await getSyncOperationService().logBatch(ops);
          }
        }
      });

      // Hook: afterAdd (outside transaction - entity is committed)
      await this.afterAdd?.(entity);

      return entity;
    } catch (error) {
      console.error(`Failed to add ${this.tableName}:`, error);
      throw new Error(`Failed to create ${this.tableName}`);
    }
  }

  /**
   * Get entity by ID
   *
   * Note: Returns record even if tombstoned (soft-deleted). This is intentional
   * for sync flows that need to access tombstone metadata. Use `getAll()` for
   * filtered results that exclude tombstones.
   *
   * @param id - Entity ID
   * @returns Promise resolving to entity or undefined if not found
   */
  async get(id: string): Promise<T | undefined> {
    try {
      return await this.table.get(id);
    } catch (error) {
      console.error(`Failed to get ${this.tableName} with id ${id}:`, error);
      throw new Error(`Failed to retrieve ${this.tableName}`);
    }
  }

  /**
   * Get all entities, excluding tombstoned (soft-deleted) records
   *
   * For sync/export flows that need to include tombstones, use
   * `getAllIncludingDeleted()` instead.
   *
   * @returns Promise resolving to array of all non-deleted entities
   */
  async getAll(): Promise<T[]> {
    try {
      const allRecords = await this.table.toArray();
      // Filter out tombstoned records using isTombstoned semantics:
      // typeof deletedAt === 'string' && deletedAt.length > 0
      return allRecords.filter(record => {
        const deletable = record as unknown as SyncDeletable;
        const isTombstoned =
          typeof deletable.deletedAt === 'string' && deletable.deletedAt.length > 0;
        return !isTombstoned;
      });
    } catch (error) {
      console.error(`Failed to get all ${this.tableName}:`, error);
      throw new Error(`Failed to retrieve ${this.tableName} list`);
    }
  }

  /**
   * Get all entities including tombstoned (soft-deleted) records
   *
   * Use this method for sync/export flows that need access to tombstones
   * for proper cross-device deletion propagation.
   *
   * @returns Promise resolving to array of all entities including tombstones
   */
  async getAllIncludingDeleted(): Promise<T[]> {
    try {
      return await this.table.toArray();
    } catch (error) {
      console.error(`Failed to get all ${this.tableName} including deleted:`, error);
      throw new Error(`Failed to retrieve ${this.tableName} list`);
    }
  }

  /**
   * Update entity by ID
   *
   * Logs 'set' or 'clear' operations for each field in changes if table is in SYNC_TABLES.
   * Data write and op logging are atomic (same transaction).
   * Note: updatedAt is NOT logged - it's system metadata, not user intent.
   *
   * @param id - Entity ID
   * @param changes - Partial entity data to update
   * @returns Promise resolving to updated entity
   */
  async update(id: string, changes: Partial<T>): Promise<T> {
    // Hook: beforeUpdate (outside transaction - can abort before any changes)
    await this.beforeUpdate?.(id, changes);

    const updatedData = {
      ...changes,
      updatedAt: new Date().toISOString(),
    };

    try {
      let updatedEntity: T | undefined;

      // Wrap data write + op logging in single transaction for atomicity
      // Note: logBatch() uses [db.sync_state, db.sync_operations] which is a subset of our tables,
      // so Dexie will join the nested transaction into this one (per Dexie docs).
      await db.transaction('rw', [this.table, db.sync_operations, db.sync_state], async () => {
        markAsInstrumentedWriteContext(); // Suppress safety-net hook warnings

        // Read existing entity for diff-based op logging (fail fast if not found)
        const existingEntity = await this.table.get(id);
        if (!existingEntity) {
          throw new Error(`${this.tableName} with id ${id} not found`);
        }

        // Dexie's update method has strict typing requirements for UpdateSpec<T>
        // We need to cast through unknown to satisfy TypeScript while maintaining runtime safety
        await this.table.update(id, updatedData as unknown as Parameters<typeof this.table.update>[1]);

        // Log ops for sync tables only - diff-based to capture only actual changes
        // Uses original `changes`, NOT `updatedData` (updatedAt is system metadata)
        if (this.isSyncTable()) {
          const ops = buildDiffOps(
            this.tableName as SyncTableName,
            id,
            existingEntity as unknown as Record<string, unknown>,
            changes as unknown as Record<string, unknown>
          );
          if (ops.length > 0) {
            await getSyncOperationService().logBatch(ops);
          }
        }

        // Fetch the updated entity within transaction
        updatedEntity = await this.table.get(id);
      });

      if (!updatedEntity) {
        throw new Error(`Failed to retrieve updated ${this.tableName}`);
      }

      // Hook: afterUpdate (outside transaction - entity is committed)
      await this.afterUpdate?.(updatedEntity);

      return updatedEntity;
    } catch (error) {
      console.error(`Failed to update ${this.tableName} with id ${id}:`, error);
      throw new Error(`Failed to update ${this.tableName}`);
    }
  }

  /**
   * Soft-delete entity by ID (tombstone pattern for sync-aware deletion)
   *
   * Sets `deletedAt` and `deletedByDevice` fields instead of removing the record,
   * allowing the deletion to propagate to other devices via sync.
   *
   * Logs a 'delete' operation for sync if table is in SYNC_TABLES.
   * Tombstone write and op logging are atomic (same transaction).
   *
   * This method is only available for entities that extend `SyncDeletable`.
   * The `this` parameter typing enforces this at compile time.
   *
   * **Important**: This method delegates to `softDeleteRecord()` which only supports
   * tables in `SYNC_TABLES`. Calling this on a non-sync table will throw at runtime.
   * All current BaseService subclasses that use softDelete (InstrumentService,
   * PreTradingSettingsService) operate on SYNC_TABLES, so this constraint is satisfied.
   *
   * Idempotent: If record is already tombstoned, this is a no-op (no error,
   * no timestamp update). This prevents race conditions in multi-tab scenarios.
   *
   * @param id - Entity ID to soft-delete
   * @throws Error if record does not exist
   * @throws Error if table is not in SYNC_TABLES (runtime guard)
   */
  async softDelete(
    this: BaseService<T & SyncDeletable>,
    id: string
  ): Promise<void> {
    // Step 1: Check if entity exists (before hooks - no side effects if not found)
    const entity = await this.get(id);
    if (!entity) {
      throw new Error(`${this.tableName} with id ${id} not found`);
    }

    // Step 2: Idempotent check - if already tombstoned, true no-op (no hooks, no side effects)
    // Uses isTombstoned semantics: typeof deletedAt === 'string' && deletedAt.length > 0
    const alreadyTombstoned =
      typeof entity.deletedAt === 'string' && entity.deletedAt.length > 0;
    if (alreadyTombstoned) {
      return;
    }

    try {
      // Step 3: Call beforeDelete hook (only after confirming we will make a change)
      await this.beforeDelete?.(id);

      let wasDeleted = false;

      // Step 4: Wrap tombstone write + op logging in single transaction for atomicity
      // softDeleteRecord() uses direct table.update(), so wrapping is safe (no nested transaction)
      // Note: logOperation() uses [db.sync_state, db.sync_operations] which is a subset of our tables,
      // so Dexie will join the nested transaction into this one (per Dexie docs).
      await db.transaction('rw', [this.table, db.sync_operations, db.sync_state], async () => {
        markAsInstrumentedWriteContext(); // Suppress safety-net hook warnings
        // Delegate to softDeleteRecord for core tombstone logic
        // Use notify: false to control timing (after afterDelete hook)
        const result = await softDeleteRecord(
          this.tableName as SyncTableName,
          id,
          { notify: false }
        );

        wasDeleted = result.deleted;

        // Log delete op for sync tables only (and only if tombstone was applied)
        if (wasDeleted && this.isSyncTable()) {
          await getSyncOperationService().logOperation({
            table: this.tableName as SyncTableName,
            recordId: id,
            op: 'delete',
          });
        }
      });

      // Step 5: Only call afterDelete and notify if we actually tombstoned
      // (handles race condition where another process tombstoned between our check and theirs)
      if (wasDeleted) {
        await this.afterDelete?.(id);
        syncCoordinatorService.notifyChange(this.tableName, id);
      }
    } catch (error) {
      console.error(`Failed to soft-delete ${this.tableName} with id ${id}:`, error);
      throw error;
    }
  }

  /**
   * Hard-delete entity by ID (permanent removal from database)
   *
   * Use this for tombstone cleanup after sync has propagated the deletion,
   * or for entities that don't participate in sync.
   *
   * Does NOT broadcast via SyncCoordinatorService (cleanup of already-synced
   * tombstones should not trigger additional sync events).
   *
   * NOTE: Intentionally NOT marked as instrumented write context. This means
   * the safety-net Dexie hooks will log a dev-only warning when hardDelete()
   * is called on sync tables. This is a guardrail - hardDelete should only be
   * used for post-sync cleanup, and the warning alerts developers if it's
   * being used unexpectedly (e.g., instead of softDelete).
   *
   * @param id - Entity ID
   * @returns Promise that resolves when deletion is complete
   */
  async hardDelete(id: string): Promise<void> {
    // TODO: [CONTRACTOR] Dependency checks happen here
    await this.beforeDelete?.(id);

    try {
      await this.table.delete(id);

      // TODO: [CONTRACTOR] Cleanup operations happen here
      await this.afterDelete?.(id);
    } catch (error) {
      console.error(`Failed to hard-delete ${this.tableName} with id ${id}:`, error);
      throw new Error(`Failed to delete ${this.tableName}`);
    }
  }

  /**
   * Delete entity by ID (alias for hardDelete)
   *
   * @deprecated Use `softDelete()` for sync-aware tombstone deletion, or
   * `hardDelete()` for permanent removal. This method will be removed in
   * a future version.
   *
   * @param id - Entity ID
   * @returns Promise that resolves when deletion is complete
   */
  async delete(id: string): Promise<void> {
    return this.hardDelete(id);
  }

  // ===== UTILITY METHODS =====

  /**
   * Check if entity exists by ID
   * 
   * @param id - Entity ID
   * @returns Promise resolving to boolean
   */
  async exists(id: string): Promise<boolean> {
    try {
      const count = await this.table.where('id').equals(id).count();
      return count > 0;
    } catch (error) {
      console.error(`Failed to check existence of ${this.tableName} with id ${id}:`, error);
      return false;
    }
  }

  /**
   * Get entity count
   * 
   * @returns Promise resolving to total number of entities
   */
  async count(): Promise<number> {
    try {
      return await this.table.count();
    } catch (error) {
      console.error(`Failed to count ${this.tableName}:`, error);
      throw new Error(`Failed to count ${this.tableName}`);
    }
  }

  /**
   * Bulk add entities (useful for imports/migrations)
   *
   * Logs 'set' operations for all fields of all entities if table is in SYNC_TABLES.
   * All ops share one batchId - represents atomic bulk operation (e.g., onboarding).
   * Data write and op logging are atomic (same transaction).
   *
   * @param entities - Array of entity data
   * @returns Promise resolving to array of created entities
   */
  async bulkAdd(entities: Array<Partial<T>>): Promise<T[]> {
    const now = new Date().toISOString();

    const entitiesWithIds: T[] = entities.map(data => ({
      ...data,
      id: generateUUID(),
      createdAt: now,
      updatedAt: now,
    } as T));

    try {
      // Wrap data write + op logging in single transaction for atomicity
      // Note: logBatch() uses [db.sync_state, db.sync_operations] which is a subset of our tables,
      // so Dexie will join the nested transaction into this one (per Dexie docs).
      await db.transaction('rw', [this.table, db.sync_operations, db.sync_state], async () => {
        markAsInstrumentedWriteContext(); // Suppress safety-net hook warnings
        // table.bulkAdd() throws on first error by default, which aborts transaction
        await this.table.bulkAdd(entitiesWithIds);

        // Log ops for sync tables only
        if (this.isSyncTable()) {
          // Build ops for all entities - one batchId for the entire bulk operation
          const allOps = entitiesWithIds.flatMap(entity =>
            buildSetOps(
              this.tableName as SyncTableName,
              entity.id,
              entity as unknown as Record<string, unknown>
            )
          );

          if (allOps.length > 0) {
            await getSyncOperationService().logBatch(allOps);
          }
        }
      });

      return entitiesWithIds;
    } catch (error) {
      console.error(`Failed to bulk add ${this.tableName}:`, error);
      throw new Error(`Failed to bulk create ${this.tableName}`);
    }
  }

  // ===== CONTRACTOR HOOK SETTERS =====
  // These will be called by contractor integration code

  /** Set license validation hook */
  setBeforeAddHook(hook: (data: Partial<T>) => Promise<void>): void {
    this.beforeAdd = hook;
  }

  /** Set analytics tracking hook */
  setAfterAddHook(hook: (entity: T) => Promise<void>): void {
    this.afterAdd = hook;
  }

  /** Set update permission hook */
  setBeforeUpdateHook(hook: (id: string, changes: Partial<T>) => Promise<void>): void {
    this.beforeUpdate = hook;
  }

  /** Set sync trigger hook */
  setAfterUpdateHook(hook: (entity: T) => Promise<void>): void {
    this.afterUpdate = hook;
  }

  /** Set dependency check hook */
  setBeforeDeleteHook(hook: (id: string) => Promise<void>): void {
    this.beforeDelete = hook;
  }

  /** Set cleanup hook */
  setAfterDeleteHook(hook: (id: string) => Promise<void>): void {
    this.afterDelete = hook;
  }
}